import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import Empty from '@/assets/icons/empty.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';

type DialogState = {
  openingTitle: string;
  hiringGoalUrl?: string;
};

type OpeningDetailsParams = {
  title: string;
  brandSlug?: string | null;
  locationId?: string;
  funnelId?: string;
};

type NoHiringGoalDialogContextType = {
  openNoHiringGoalDialog: (opening: OpeningDetailsParams) => void;
};

const NoHiringGoalDialogContext = createContext<
  NoHiringGoalDialogContextType | undefined
>(undefined);

export function NoHiringGoalDialogProvider({ children }: PropsWithChildren) {
  const { getHireATHiringGoalUrl } = useHireUrl();
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const openNoHiringGoalDialog = useCallback(
    ({ title, brandSlug, locationId, funnelId }: OpeningDetailsParams) => {
      const hiringGoalUrl =
        locationId &&
        funnelId &&
        getHireATHiringGoalUrl(brandSlug ?? 'no_brand', locationId, funnelId);

      setDialogState({
        openingTitle: title,
        hiringGoalUrl,
      });
    },
    [getHireATHiringGoalUrl],
  );

  const handleClose = () => {
    setDialogState(null);
  };

  return (
    <NoHiringGoalDialogContext.Provider
      value={{
        openNoHiringGoalDialog,
      }}
    >
      {children}
      {dialogState && (
        <Dialog
          open
          onOpenChange={open => {
            if (!open) handleClose();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <FormattedMessage defaultMessage="Hiring goal required!" />
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center gap-2 px-6 py-4">
              <img src={Empty} alt="Empty" />
              <Typography variant="body2">
                <FormattedMessage defaultMessage="Add a hiring goal to get started" />
              </Typography>

              <Typography variant="subtitle1" className="text-center">
                <FormattedMessage
                  defaultMessage="To get started with {openingTitle}, please add a hiring goal so we can track progress and align your sourcing efforts."
                  values={{
                    openingTitle: <strong>{dialogState.openingTitle}</strong>,
                  }}
                />
              </Typography>
            </div>

            <DialogFooter>
              {dialogState.hiringGoalUrl ? (
                <Button asChild>
                  <Link to={dialogState.hiringGoalUrl} target="_blank">
                    <ExternalLinkIcon />
                    <FormattedMessage defaultMessage="Add hiring goal" />
                  </Link>
                </Button>
              ) : (
                <Button disabled>
                  <ExternalLinkIcon />
                  <FormattedMessage defaultMessage="Add hiring goal" />
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </NoHiringGoalDialogContext.Provider>
  );
}

export function useNoHiringGoalDialog() {
  const context = useContext(NoHiringGoalDialogContext);

  if (!context) {
    throw new Error(
      'useNoHiringGoalDialog must be used within a NoHiringGoalDialogProvider',
    );
  }

  return context;
}
